import React, { createRef, useCallback, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Grid, type Theme, useMediaQuery } from '@mui/material'
import type { Nil } from '@pbt/pbt-ui-components'

import { KibSearchWrapper } from '~/components/common/search/KibSearchWrapper'
import { clearSearchOrders } from '~/store/actions/orders'
import {
  getOrdersSearchResults,
  getOrdersSearchResultsTotalCount,
} from '~/store/reducers/orders'
import type { Order } from '~/types'
import { TrackingEvent, useTrackSearch } from '~/utils/trackingProvider'

import { useSwapProductSearch } from '../hooks/useSwapProductSearch'
import SwapRxSearchResults from './SwapRxSearchResults'

const SwapRxSearch = ({
  patientId,
  clientId,
  selectedOrder,
  setSelectedOrder,
}: {
  clientId: string | Nil
  patientId: string | Nil
  selectedOrder: Order | null
  setSelectedOrder: (selectedOrder: Order | null) => void
}) => {
  const dispatch = useDispatch()
  const { addSearchAttempt, trackSearch, startSearch } = useTrackSearch(
    TrackingEvent.SEARCH_CHARGE_TAB,
    'selectedItemVariationId',
  )

  const isMedium = useMediaQuery<Theme>((theme) => theme.breakpoints.down('lg'))

  const searchResults = useSelector(getOrdersSearchResults)
  const searchResultsTotalCount = useSelector(getOrdersSearchResultsTotalCount)

  const itemsRefs = Array.from({ length: searchResults.length }, () =>
    createRef<any>(),
  )

  const validate = () =>
    itemsRefs.every((ref) =>
      ref.current?.validate ? ref.current.validate() : true,
    )

  const {
    onSearchChange,
    onSearchBlur,
    onSearchFocus,
    searchDisabled,
    searchTerm,
    onSearchMore,
    onSearchClear,
  } = useSwapProductSearch({
    validate,
    patientId,
    clientId,
    onSearch: addSearchAttempt,
    onFocus: startSearch,
  })

  const searchTermRef = useRef(searchTerm)
  const searchResultsTotalCountRef = useRef(searchResultsTotalCount)
  const selectedOrderNameRef = useRef<string | null>(null)

  const handleSelectOrder = useCallback(
    (order: Order) => {
      selectedOrderNameRef.current = order.name
      setSelectedOrder(order)
      dispatch(clearSearchOrders())
    },
    [setSelectedOrder, dispatch],
  )

  const handleSearchChange = useCallback(
    (value: string) => {
      if (selectedOrderNameRef.current) {
        if (value === selectedOrderNameRef.current || value === searchTerm)
          return
        selectedOrderNameRef.current = null
        setSelectedOrder(null)
      }

      onSearchChange(value)
    },
    [setSelectedOrder, onSearchChange, searchTerm],
  )

  const handleClearSearch = useCallback(() => {
    selectedOrderNameRef.current = null
    onSearchClear()
    setSelectedOrder(null)
  }, [setSelectedOrder, onSearchClear])

  useEffect(() => {
    searchTermRef.current = searchTerm
  }, [searchTerm])

  useEffect(() => {
    searchResultsTotalCountRef.current = searchResultsTotalCount
  }, [searchResultsTotalCount])

  return (
    <Grid container p={2}>
      <Grid
        container
        item
        alignItems="baseline"
        width="100%"
        wrap={isMedium ? 'wrap-reverse' : 'nowrap'}
        zIndex={1}
      >
        <Grid container position="relative">
          <KibSearchWrapper
            showCloseButton
            disabled={searchDisabled}
            placeholder="Search catalog"
            value={selectedOrder?.name || searchTerm}
            onBlur={onSearchBlur}
            onChange={handleSearchChange}
            onClear={handleClearSearch}
            onFocus={onSearchFocus}
          />
          {patientId && clientId && (
            <SwapRxSearchResults
              searchTerm={searchTerm}
              selectedOrder={selectedOrder}
              setSelectedOrder={handleSelectOrder}
              onSearch={onSearchChange}
              onSearchMore={onSearchMore}
            />
          )}
        </Grid>
      </Grid>
    </Grid>
  )
}

export default SwapRxSearch
